import { captureException, logger as sentryLogger } from "@sentry/react";
import {
	createRootRouteWithContext,
	HeadContent,
	Outlet,
	useRouterState,
} from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { AutoUpdater } from "../components/app/auto-updater";
import { DeepLinkHandler } from "../components/app/deep-link-handler";
import Loader from "../components/loader";
import { ThemeProvider } from "../components/theme-provider";
import { Toaster } from "../components/ui/sonner";

export type RouterAppContext = Record<string, never>;

export const Route = createRootRouteWithContext<RouterAppContext>()({
	errorComponent: ({ error }) => {
		captureException(error);
		sentryLogger.error("Unhandled route error", {
			message: error instanceof Error ? error.message : String(error),
		});
		return (
			<div className="flex h-svh flex-col items-center justify-center gap-4">
				<h1 className="font-bold text-2xl">Something went wrong</h1>
				<p className="text-muted-foreground">
					An unexpected error occurred. Please try refreshing the page.
				</p>
				<button
					className="rounded-md bg-primary px-4 py-2 text-primary-foreground"
					onClick={() => window.location.reload()}
					type="button"
				>
					Refresh Page
				</button>
			</div>
		);
	},
	component: RootDocument,
});

function RootDocument() {
	// The full-screen spinner covers only the first load, before any page has
	// resolved. The router is loading for a moment on every later navigation
	// too, and swapping the tree for a spinner then tore down and rebuilt every
	// client on every click: the Organisation window opened its Organisation
	// again, recording another visit, on each sidebar click. A page slow to load
	// shows the router's own pending component in its place instead.
	const isFirstLoad = useRouterState({
		select: (s) => s.isLoading && !s.resolvedLocation,
	});
	// The routes that want a single full-height pane they size themselves, rather
	// than the auto-then-fill grid every other route gets. Both admin clients are
	// here with `/operations` because they are the same shape of surface (a fixed
	// header over a scrolling body) and because their guard states are `h-full`,
	// which collapses in an `auto`-sized grid row.
	const isAppRoute = useRouterState({
		select: (s) =>
			s.location.pathname.startsWith("/app") ||
			s.location.pathname.startsWith("/operations") ||
			s.location.pathname.startsWith("/organization") ||
			s.location.pathname.startsWith("/instance"),
	});

	return (
		<ThemeProvider
			attribute="class"
			defaultTheme="system"
			disableTransitionOnChange
			enableSystem
		>
			<HeadContent />
			<div className="flex h-svh flex-col overflow-hidden">
				{/* TODO: Re-enable when desktop app is ready for distribution */}
				{/* <OpenInAppBanner /> */}
				{isAppRoute ? (
					<div className="min-h-0 shrink grow">
						{isFirstLoad ? <Loader /> : <Outlet />}
					</div>
				) : (
					<div className="grid shrink grow grid-rows-[auto_1fr]">
						{isFirstLoad ? <Loader /> : <Outlet />}
					</div>
				)}
			</div>
			<Toaster richColors />
			<AutoUpdater />
			<DeepLinkHandler />
			<TanStackRouterDevtools position="bottom-left" />
		</ThemeProvider>
	);
}
